import { useCallback } from 'react';
import type { ProxySettings } from '../store/proxy-store';
import { useProxyStore } from '../store/proxy-store';

/**
 * Shared hook for settings helper functions.
 * Uses Zustand's getState() to avoid stale closure issues and prevent
 * unnecessary callback recreation when settings change.
 */
export function useSettingsHelpers() {
  const settings = useProxyStore((state) => state.settings);
  const updateSettings = useProxyStore((state) => state.updateSettings);

  const updateSetting = useCallback(
    <K extends keyof ProxySettings>(key: K, value: ProxySettings[K]) => {
      updateSettings({ [key]: value });
    },
    [updateSettings],
  );

  const updateNestedSetting = useCallback(
    <K extends keyof ProxySettings>(
      key: K,
      nestedKey: string,
      value: unknown,
    ) => {
      const currentValue = useProxyStore.getState().settings[key];
      const baseValue =
        typeof currentValue === 'object' && currentValue !== null
          ? currentValue
          : {};
      updateSettings({
        [key]: { ...baseValue, [nestedKey]: value },
      } as Partial<ProxySettings>);
    },
    [updateSettings],
  );

  const addToArraySetting = useCallback(
    <K extends keyof ProxySettings>(key: K, value: unknown) => {
      const currentValue = useProxyStore.getState().settings[key];
      if (Array.isArray(currentValue)) {
        updateSettings({
          [key]: [...currentValue, value],
        } as Partial<ProxySettings>);
      }
    },
    [updateSettings],
  );

  const removeFromArraySetting = useCallback(
    <K extends keyof ProxySettings>(key: K, index: number) => {
      const currentValue = useProxyStore.getState().settings[key];
      if (Array.isArray(currentValue)) {
        updateSettings({
          [key]: currentValue.filter((_, i) => i !== index),
        } as Partial<ProxySettings>);
      }
    },
    [updateSettings],
  );

  return {
    settings,
    updateSettings,
    updateSetting,
    updateNestedSetting,
    addToArraySetting,
    removeFromArraySetting,
  };
}
